// useChat — manages the single SignalR connection to /chathub (cookie auth rides
// along, same-origin). Exposes connection state, the invoke methods, and lets the
// caller register handlers for server-pushed events without re-subscribing on
// every render.

import { useCallback, useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { wirePresenceBeats } from "../lib/presenceBeat.js";

export function useChat(handlers) {
  const connRef = useRef(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const [state, setState] = useState("connecting");

  useEffect(() => {
    const conn = new signalR.HubConnectionBuilder()
      .withUrl("/chathub")
      // Retry FOREVER (capped backoff) instead of the default policy that gives up after ~30s.
      // A deploy / idle recycle can take longer than that to restart; without this the socket dies
      // permanently, live messages are silently missed, and the open channel goes stale until a
      // manual refresh. Retrying indefinitely means it always recovers → onReconnected re-syncs.
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) => Math.min(2000 * (ctx.previousRetryCount + 1), 15000),
      })
      // Stateful reconnect: a brief blip RESUMES this connection (same connection id,
      // buffered messages replayed) instead of tearing it down and starting a new one.
      .withStatefulReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    // Office wifi jitter was tripping the default 30s server timeout constantly. Ping more
    // often and wait longer before declaring the connection dead — but stay under the server's
    // 60s window (Program.cs), so a dead socket is noticed in time to resume, not rebuilt.
    conn.serverTimeoutInMilliseconds = 45000;
    conn.keepAliveIntervalInMilliseconds = 10000;
    connRef.current = conn;

    conn.on("ReceiveMessage", (m) => handlersRef.current.onReceive?.(m));
    conn.on("MessageEdited", (m) => handlersRef.current.onEdited?.(m));
    conn.on("MessageDeleted", (m) => handlersRef.current.onDeleted?.(m));
    conn.on("MessageMoved", (e) => handlersRef.current.onMoved?.(e));
    // A newer card replaced older ones (a ticket's status updates collapse to one card) — the old
    // ids just disappear from the stream; no "deleted" tombstone.
    conn.on("MessagesSuperseded", (e) => handlersRef.current.onSuperseded?.(e));
    conn.on("Typing", (t) => handlersRef.current.onTyping?.(t));
    conn.on("ReadReceipt", (r) => handlersRef.current.onRead?.(r));
    conn.on("MembershipChanged", (e) => handlersRef.current.onMembership?.(e));
    conn.on("Mentioned", (e) => handlersRef.current.onMentioned?.(e));
    conn.on("MeetingUpdated", (e) => handlersRef.current.onMeetingUpdated?.(e));
    conn.on("MeetingParticipants", (e) => handlersRef.current.onMeetingParticipants?.(e));
    conn.on("MeetingReminder", (e) => handlersRef.current.onMeetingReminder?.(e));
    conn.on("Nudged", (e) => handlersRef.current.onNudged?.(e));
    conn.on("Jumpscared", (e) => handlersRef.current.onJumpscared?.(e));
    conn.on("TicketDeleted", (e) => handlersRef.current.onTicketDeleted?.(e));
    conn.on("ReactionChanged", (e) => handlersRef.current.onReactionChanged?.(e));

    conn.onreconnecting(() => setState("reconnecting"));
    // On reconnect the server re-adds our channel groups (new connection → OnConnectedAsync),
    // but messages sent WHILE we were disconnected weren't delivered — let the caller re-fetch
    // history to close the gap (the instance recycles on deploy / idle scale-to-zero).
    conn.onreconnected(() => { setState("connected"); handlersRef.current.onReconnected?.(); });
    conn.onclose(() => setState("disconnected"));

    // withAutomaticReconnect only engages AFTER a successful first connect — so retry the initial
    // start ourselves (until this effect is torn down) so a cold-starting backend still connects.
    let stopped = false;
    let unwireBeats = null;
    const startWithRetry = async (attempt = 0) => {
      if (stopped) return;
      try {
        await conn.start();
        setState("connected");
        // Visible-tab beats → active presence (drives OS-push suppression server-side).
        if (!unwireBeats) unwireBeats = wirePresenceBeats(conn);
      }
      catch {
        setState("disconnected");
        setTimeout(() => startWithRetry(attempt + 1), Math.min(2000 * (attempt + 1), 15000));
      }
    };
    startWithRetry();
    return () => { stopped = true; unwireBeats?.(); conn.stop(); };
  }, []);

  // ticketRefs: tagged tickets [{ id, issueId, taskId, name, isBug }] snapshotted into the
  // message's metadata and rendered as reference chips above it.
  const send = useCallback(
    (channelId, content, type = 0, mediaUrl = null, replyToId = null, mentionedUserIds = null, ticketRefs = null, channelRefs = null, threadRootId = null) =>
      connRef.current?.invoke("SendMessage", channelId, content, type, mediaUrl, replyToId, mentionedUserIds, ticketRefs, channelRefs, threadRootId),
    [],
  );
  const edit = useCallback((id, content) => connRef.current?.invoke("EditMessage", id, content), []);
  const remove = useCallback((id) => connRef.current?.invoke("DeleteMessage", id), []);
  // Forward a message into one or more other channels (server re-posts it with a "forwarded from" header).
  const forward = useCallback((fromMessageId, toChannelIds) => connRef.current?.invoke("ForwardMessage", fromMessageId, toChannelIds), []);
  const toggleReaction = useCallback((messageId, emoji) => connRef.current?.invoke("ToggleReaction", messageId, emoji), []);
  const typing = useCallback((channelId) => connRef.current?.invoke("Typing", channelId), []);
  const markRead = useCallback((channelId, messageId) => connRef.current?.invoke("MarkRead", channelId, messageId), []);
  const joinChannel = useCallback((channelId) => connRef.current?.invoke("JoinChannel", channelId), []);
  const leaveChannel = useCallback((channelId) => connRef.current?.invoke("LeaveChannel", channelId), []);

  return { state, send, edit, remove, forward, toggleReaction, typing, markRead, joinChannel, leaveChannel };
}
